import { useState, useEffect } from "react";
import crudApi from "@serie3/common/api/crudApi";
import DocumentsEndpoints from "@serie3/mymediaplanner/Modules/Documents/endpoints";
import NewModal from "../../../shared/Modals/NewModal";

/**
 * Files attached to the job's linked post.
 *
 * The job itself never carries files — its `assets` are paths on the machine.
 * Anything viewable here lives on the Post, so this fetches by `post_id` and
 * reuses the documents preview endpoint the file manager uses.
 */
const JobPostFiles = ({ postId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [preview, setPreview] = useState(null); // {url, type}
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadFiles = async () => {
      if (!postId) {
        setFiles([]);
        return;
      }
      setLoading(true);
      const response = await crudApi.getAll(
        DocumentsEndpoints.showPostFiles + postId,
      );
      if (!cancelled) {
        setFiles(response?.ok ? (response.data ?? []) : []);
        setLoading(false);
      }
    };

    loadFiles();
    return () => {
      cancelled = true;
    };
  }, [postId]);

  // Blobs are created per preview; release the previous one before replacing it.
  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const openPreview = async (file) => {
    setPreview(null);
    setPreviewFile(file);
    setPreviewLoading(true);
    setShowPreview(true);

    const response = await crudApi.getBinary(DocumentsEndpoints.show + file.id);
    const type = response.headers["content-type"];
    const blob = new Blob([response.data], { type });
    setPreview({ url: URL.createObjectURL(blob), type });
    setPreviewLoading(false);
  };

  if (!postId) return null;
  if (loading) {
    return <div className="text-muted font-size-12">Loading files…</div>;
  }
  if (files.length === 0) {
    return (
      <div className="text-muted font-size-12">
        No files attached to the linked post.
      </div>
    );
  }

  return (
    <>
      <ul className="list-unstyled mb-0">
        {files.map((file) => (
          <li key={file.id} className="mb-1">
            <button
              type="button"
              className="btn btn-link p-0 align-baseline text-start"
              onClick={() => openPreview(file)}
            >
              {file.original_name}
            </button>
            {file.size && (
              <span className="text-muted font-size-11 ms-2">
                {(file.size / 1024).toFixed(1)} KB
              </span>
            )}
          </li>
        ))}
      </ul>

      <NewModal
        show={showPreview}
        setShow={setShowPreview}
        title={previewFile?.original_name ?? "File"}
        size="xl"
      >
        {previewLoading && <div className="text-muted">Loading file…</div>}

        {preview && (
          <div className="text-center">
            {(preview.type || "").startsWith("image/") && (
              <img
                src={preview.url}
                alt={previewFile?.original_name}
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
            )}

            {(preview.type || "").startsWith("video/") && (
              <video
                src={preview.url}
                controls
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
            )}

            {(preview.type || "").startsWith("audio/") && (
              <audio src={preview.url} controls style={{ width: "100%" }} />
            )}

            {preview.type === "application/pdf" && (
              <iframe
                src={preview.url}
                title={previewFile?.original_name}
                style={{ width: "100%", height: "70vh", border: 0 }}
              />
            )}

            {!(preview.type || "").startsWith("image/") &&
              !(preview.type || "").startsWith("video/") &&
              !(preview.type || "").startsWith("audio/") &&
              preview.type !== "application/pdf" && (
                <a href={preview.url} download={previewFile?.original_name}>
                  Download {previewFile?.original_name}
                </a>
              )}
          </div>
        )}
      </NewModal>
    </>
  );
};

export default JobPostFiles;
